// frontend/src/context/ThemeContext.jsx
// Theme and Preferences Context with GraphQL Backend Sync
// ========================================================
// UPDATED: February 24, 2026 - B38 Fix
// Changed: REST API → GraphQL for notification preferences
// Added: Load preferences from backend on init
// Added: Proper Apollo client integration
// ========================================================

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';

const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// ============================================
// GRAPHQL OPERATIONS
// ============================================

const GET_NOTIFICATION_PREFERENCES = gql`
  query GetNotificationPreferences {
    notificationPreferences {
      id
      userId
      email {
        enabled
        types {
          price_drop
          group_joined
          purchase_update
          payment_confirmed
          delivery_scheduled
          delivery_completed
          refund_processed
          token_earned
          group_milestone
          vendor_update
          deals_alerts
          weekly_digest
          marketing
        }
      }
      sms {
        enabled
        phoneNumber
        verified
        types {
          delivery_scheduled
          delivery_completed
          payment_confirmed
        }
      }
      push {
        enabled
        deviceTokens
        types {
          price_drop
          group_joined
          purchase_update
          token_earned
        }
      }
      inApp {
        enabled
        showBadge
        playSound
      }
      quietHours {
        enabled
        start
        end
        timezone
      }
      createdAt
      updatedAt
    }
  }
`;

const UPDATE_NOTIFICATION_PREFERENCES = gql`
  mutation UpdateNotificationPreferences($input: NotificationPreferencesInput!) {
    updateNotificationPreferences(input: $input) {
      success
      message
      preferences {
        id
        userId
        email {
          enabled
          types {
            price_drop
            group_joined
            purchase_update
            payment_confirmed
            delivery_scheduled
            delivery_completed
            refund_processed
            token_earned
            group_milestone
            vendor_update
            deals_alerts
            weekly_digest
            marketing
          }
        }
        sms {
          enabled
          phoneNumber
          verified
        }
        push {
          enabled
        }
        inApp {
          enabled
          showBadge
          playSound
        }
      }
    }
  }
`;

// ============================================
// DEFAULT PREFERENCES
// ============================================

const DEFAULT_PREFERENCES = {
  // Appearance (local only)
  largeText: false,
  reducedMotion: false,
  highContrast: false,
  
  // Notifications - main toggles (synced to backend)
  pushNotifications: true,
  emailNotifications: true,
  smsNotifications: false,
  
  // Email specific preferences (synced to backend)
  emailOrderUpdates: true,
  emailDealsAlerts: true,
  emailWeeklyDigest: true,
  emailMarketing: false,
  
  // Privacy (local only for now)
  showTrustBadges: true,
  showActivityStatus: true,
  showOnLeaderboard: true,
  
  // Sound (synced to backend via inApp.playSound)
  soundEnabled: true
};

// ============================================
// HELPER FUNCTIONS
// ============================================

// Map frontend prefs to backend GraphQL format
const mapPreferencesToBackend = (frontendPrefs) => {
  return {
    email: {
      enabled: frontendPrefs.emailNotifications,
      types: {
        purchase_update: frontendPrefs.emailOrderUpdates,
        deals_alerts: frontendPrefs.emailDealsAlerts,
        weekly_digest: frontendPrefs.emailWeeklyDigest,
        marketing: frontendPrefs.emailMarketing
      }
    },
    sms: {
      enabled: frontendPrefs.smsNotifications
    },
    push: {
      enabled: frontendPrefs.pushNotifications
    },
    inApp: {
      enabled: true,
      showBadge: true,
      playSound: frontendPrefs.soundEnabled ?? true
    }
  };
};

// Map backend GraphQL response to frontend format
const mapPreferencesToFrontend = (backendPrefs) => {
  if (!backendPrefs) return {};
  
  return {
    emailNotifications: backendPrefs.email?.enabled ?? true,
    emailOrderUpdates: backendPrefs.email?.types?.purchase_update ?? true,
    emailDealsAlerts: backendPrefs.email?.types?.deals_alerts ?? true,
    emailWeeklyDigest: backendPrefs.email?.types?.weekly_digest ?? true,
    emailMarketing: backendPrefs.email?.types?.marketing ?? false,
    smsNotifications: backendPrefs.sms?.enabled ?? false,
    pushNotifications: backendPrefs.push?.enabled ?? true,
    soundEnabled: backendPrefs.inApp?.playSound ?? true
  };
};

// ============================================
// PROVIDER COMPONENT
// ============================================

export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncError, setLastSyncError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  const syncTimeoutRef = useRef(null);
  const initialLoadDone = useRef(false);

  // ============================================
  // GRAPHQL HOOKS
  // ============================================
  
  // Query to load preferences from backend
  const { data: prefsData, loading: prefsLoading, refetch: refetchPrefs } = useQuery(
    GET_NOTIFICATION_PREFERENCES,
    {
      skip: !isAuthenticated, // Only fetch when authenticated
      fetchPolicy: 'cache-and-network',
      onCompleted: (data) => {
        if (data?.notificationPreferences) {
          const backendPrefs = mapPreferencesToFrontend(data.notificationPreferences);
          setPreferences(prev => {
            const merged = { ...prev, ...backendPrefs };
            // Save merged prefs to localStorage
            localStorage.setItem('uniledge_preferences', JSON.stringify(merged));
            return merged;
          });
          console.log('[ThemeContext] Loaded preferences from backend');
        }
      },
      onError: (error) => {
        console.error('[ThemeContext] Error loading preferences from backend:', error);
        // Fall back to localStorage (already loaded)
      }
    }
  );

  // Mutation to update preferences on backend
  const [updatePreferencesMutation] = useMutation(UPDATE_NOTIFICATION_PREFERENCES, {
    onCompleted: (data) => {
      if (data?.updateNotificationPreferences?.success) {
        console.log('[ThemeContext] Preferences synced to backend successfully');
        setLastSyncError(null);
      } else {
        console.error('[ThemeContext] Backend sync failed:', data?.updateNotificationPreferences?.message);
        setLastSyncError(data?.updateNotificationPreferences?.message || 'Sync failed');
      }
      setIsSyncing(false);
    },
    onError: (error) => {
      console.error('[ThemeContext] Error syncing preferences to backend:', error);
      setLastSyncError(error.message);
      setIsSyncing(false);
    }
  });

  // ============================================
  // CHECK AUTHENTICATION STATUS
  // ============================================
  
  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('token');
      const isAuth = localStorage.getItem('isAuthenticated') === 'true' || 
                     localStorage.getItem('isLoggedIn') === 'true';
      setIsAuthenticated(!!(token && isAuth));
    };
    
    checkAuth();
    
    // Listen for auth changes
    window.addEventListener('storage', checkAuth);
    window.addEventListener('auth:login', checkAuth);
    window.addEventListener('auth:logout', checkAuth);
    
    return () => {
      window.removeEventListener('storage', checkAuth);
      window.removeEventListener('auth:login', checkAuth);
      window.removeEventListener('auth:logout', checkAuth);
    };
  }, []);

  // ============================================
  // LOAD LOCAL PREFERENCES ON MOUNT
  // ============================================
  
  useEffect(() => {
    const loadLocalPreferences = () => {
      try {
        // Load theme from localStorage
        const savedTheme = localStorage.getItem('uniledge_theme');
        const savedPrefs = localStorage.getItem('uniledge_preferences');
        
        if (savedTheme) {
          setIsDarkMode(savedTheme === 'dark');
        } else {
          setIsDarkMode(false);
        }
        
        if (savedPrefs) {
          try {
            const parsed = JSON.parse(savedPrefs);
            setPreferences(prev => ({ ...prev, ...parsed }));
          } catch (e) {
            console.error('[ThemeContext] Error parsing saved preferences:', e);
          }
        }
      } catch (error) {
        console.error('[ThemeContext] Error loading local preferences:', error);
      } finally {
        setIsLoading(false);
        initialLoadDone.current = true;
      }
    };
    
    loadLocalPreferences();
  }, []);

  // ============================================
  // APPLY DARK MODE TO DOCUMENT
  // ============================================
  
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      document.body.classList.add('dark-mode');
    } else {
      document.documentElement.classList.remove('dark');
      document.body.classList.remove('dark-mode');
    }
    localStorage.setItem('uniledge_theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  // ============================================
  // APPLY LARGE TEXT PREFERENCE
  // ============================================
  
  useEffect(() => {
    if (preferences.largeText) {
      document.body.classList.add('large-text');
    } else {
      document.body.classList.remove('large-text');
    }
  }, [preferences.largeText]);

  // ============================================
  // APPLY REDUCED MOTION PREFERENCE
  // ============================================
  
  useEffect(() => {
    if (preferences.reducedMotion) {
      document.body.classList.add('reduced-motion');
    } else {
      document.body.classList.remove('reduced-motion');
    }
  }, [preferences.reducedMotion]);

  // ============================================
  // SYNC TO BACKEND VIA GRAPHQL
  // ============================================
  
  const syncToBackend = useCallback(async (prefsToSync) => {
    if (!isAuthenticated) {
      console.log('[ThemeContext] Not authenticated, skipping backend sync');
      return;
    }
    
    setIsSyncing(true);
    setLastSyncError(null);
    
    try {
      const input = mapPreferencesToBackend(prefsToSync);
      await updatePreferencesMutation({ variables: { input } });
    } catch (error) {
      console.error('[ThemeContext] Error syncing to backend:', error);
      setLastSyncError(error.message);
      setIsSyncing(false);
    }
  }, [isAuthenticated, updatePreferencesMutation]);

  // ============================================
  // TOGGLE DARK MODE
  // ============================================
  
  const toggleDarkMode = useCallback(() => {
    setIsDarkMode(prev => !prev);
  }, []);

  // ============================================
  // UPDATE SINGLE PREFERENCE
  // ============================================
  
  const updatePreference = useCallback((key, value) => {
    setPreferences(prev => {
      const updated = { ...prev, [key]: value };
      
      // Save to localStorage immediately
      localStorage.setItem('uniledge_preferences', JSON.stringify(updated));
      
      // Notification keys that need backend sync
      const notificationKeys = [
        'pushNotifications', 
        'emailNotifications', 
        'smsNotifications',
        'emailOrderUpdates',
        'emailDealsAlerts',
        'emailWeeklyDigest',
        'emailMarketing',
        'soundEnabled'
      ];
      
      if (notificationKeys.includes(key)) {
        // Clear existing timeout
        if (syncTimeoutRef.current) {
          clearTimeout(syncTimeoutRef.current);
        }
        // Set new timeout for debounced sync (1 second)
        syncTimeoutRef.current = setTimeout(() => {
          syncToBackend(updated);
        }, 1000);
      }
      
      return updated;
    });
  }, [syncToBackend]);

  // ============================================
  // UPDATE MULTIPLE PREFERENCES AT ONCE
  // ============================================
  
  const updatePreferences = useCallback((updates) => {
    setPreferences(prev => {
      const updated = { ...prev, ...updates };
      localStorage.setItem('uniledge_preferences', JSON.stringify(updated));
      
      // Check if any notification preferences changed
      const notificationKeys = [
        'pushNotifications', 
        'emailNotifications', 
        'smsNotifications',
        'emailOrderUpdates',
        'emailDealsAlerts',
        'emailWeeklyDigest',
        'emailMarketing',
        'soundEnabled'
      ];
      
      const hasNotificationChanges = Object.keys(updates).some(key => 
        notificationKeys.includes(key)
      );
      
      if (hasNotificationChanges) {
        if (syncTimeoutRef.current) {
          clearTimeout(syncTimeoutRef.current);
        }
        syncTimeoutRef.current = setTimeout(() => {
          syncToBackend(updated);
        }, 1000);
      }
      
      return updated;
    });
  }, [syncToBackend]);

  // ============================================
  // RESET ALL PREFERENCES TO DEFAULTS
  // ============================================
  
  const resetPreferences = useCallback(() => {
    setPreferences(DEFAULT_PREFERENCES);
    setIsDarkMode(false);
    localStorage.setItem('uniledge_preferences', JSON.stringify(DEFAULT_PREFERENCES));
    localStorage.setItem('uniledge_theme', 'light');
    
    // Sync reset to backend
    syncToBackend(DEFAULT_PREFERENCES);
  }, [syncToBackend]);

  // ============================================
  // FORCE SYNC TO BACKEND
  // ============================================
  
  const forceSyncToBackend = useCallback(() => {
    return syncToBackend(preferences);
  }, [syncToBackend, preferences]);

  // ============================================
  // RELOAD PREFERENCES FROM BACKEND
  // ============================================
  
  const reloadFromBackend = useCallback(async () => {
    if (isAuthenticated && refetchPrefs) {
      try {
        await refetchPrefs();
      } catch (error) {
        console.error('[ThemeContext] Error reloading from backend:', error);
      }
    }
  }, [isAuthenticated, refetchPrefs]);

  // ============================================
  // CLEANUP TIMEOUT ON UNMOUNT
  // ============================================
  
  useEffect(() => {
    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, []);

  // ============================================
  // CONTEXT VALUE
  // ============================================
  
  const value = {
    // Theme
    isDarkMode,
    toggleDarkMode,
    
    // Preferences
    preferences,
    updatePreference,
    updatePreferences,
    resetPreferences,
    
    // Sync status
    isLoading: isLoading || prefsLoading,
    isSyncing,
    lastSyncError,
    isAuthenticated,
    
    // Manual sync controls
    forceSyncToBackend,
    reloadFromBackend
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;